import React from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateDealerUser } from "hooks/useDealers";
import { notify } from "utils/notify";
import { FormField } from "components/shared/FormField";
import { FormLabel } from "components/shared/FormLabel";
import { FormError, getFieldAriaProps, fieldErrorId } from "components/shared/FormError";
import {
  addUserSchema,
  addUserDefaultValues,
  userCountries,
  AddUserFormData,
} from "./schemas/addUserSchema";

interface AddUserDialogProps {
  visible: boolean;
  onHide: () => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({
  visible,
  onHide,
}) => {
  const queryClient = useQueryClient();
  const createDealerUser = useCreateDealerUser();
  const isPending = createDealerUser.isPending;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<AddUserFormData>({
    resolver: yupResolver(addUserSchema),
    defaultValues: addUserDefaultValues,
  });

  const handleCancel = () => {
    reset(addUserDefaultValues);
    onHide();
  };

  const onSubmit = (data: AddUserFormData) => {
    createDealerUser.mutate(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        dealerName: data.dealerName,
        email: data.email,
        country: data.country,
        password: data.password,
        role: "DEALER",
        isInvited: true,
      },
      {
        onSuccess: () => {
          notify.success("Dealer user created successfully");
          reset(addUserDefaultValues);
          queryClient.invalidateQueries({ queryKey: ["users"] });
          onHide();
        },
        onError: (error: Error) => {
          if (error.message.includes("already exists")) {
            notify.error("User with this email already exists");
          } else {
            notify.error(error.message || "Failed to create user");
          }
        },
      }
    );
  };

  const footer = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
        disabled={isPending}
      />
      <Button
        label="Create User"
        icon="pi pi-check"
        onClick={handleSubmit(onSubmit)}
        loading={isPending}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="Add New Dealer User"
      visible={visible}
      style={{ width: "500px" }}
      onHide={handleCancel}
      footer={footer}
      closable={!isPending}
      closeOnEscape={!isPending}
    >
      <form className="p-fluid" onSubmit={handleSubmit(onSubmit)} noValidate>
        {(
          [
            ["firstName", "First Name"],
            ["lastName", "Last Name"],
            ["dealerName", "Dealer Name"],
          ] as const
        ).map(([name, label]) => (
          <FormField key={name}>
            <FormLabel htmlFor={name}>{label}</FormLabel>
            <InputText
              {...register(name)}
              {...getFieldAriaProps(name, errors[name]?.message)}
              className={errors[name] ? "p-invalid" : ""}
              disabled={isPending}
            />
            <FormError id={fieldErrorId(name)}>{errors[name]?.message}</FormError>
          </FormField>
        ))}

        <FormField>
          <FormLabel htmlFor="country">Country</FormLabel>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Dropdown
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={userCountries}
                placeholder="Select a country"
                filter
                showClear
                className={errors.country ? "p-invalid" : ""}
                disabled={isPending}
                {...getFieldAriaProps("country", errors.country?.message)}
              />
            )}
          />
          <FormError id={fieldErrorId("country")}>{errors.country?.message}</FormError>
        </FormField>

        <FormField>
          <FormLabel htmlFor="email">Email</FormLabel>
          <InputText
            {...register("email")}
            {...getFieldAriaProps("email", errors.email?.message)}
            className={errors.email ? "p-invalid" : ""}
            disabled={isPending}
          />
          <FormError id={fieldErrorId("email")}>{errors.email?.message}</FormError>
        </FormField>

        {(
          [
            ["password", "Password", true],
            ["confirmPassword", "Repeat Password", false],
          ] as const
        ).map(([name, label, feedback]) => (
          <FormField key={name}>
            <FormLabel htmlFor={name}>{label}</FormLabel>
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <Password
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  className={errors[name] ? "p-invalid" : ""}
                  toggleMask
                  feedback={feedback}
                  disabled={isPending}
                  {...getFieldAriaProps(name, errors[name]?.message)}
                />
              )}
            />
            <FormError id={fieldErrorId(name)}>{errors[name]?.message}</FormError>
          </FormField>
        ))}

        <p style={{ fontSize: "0.9rem", color: "var(--davey-text-secondary)" }}>
          <strong>Note:</strong> This user will be created with the Dealer role.
        </p>
      </form>
    </Dialog>
  );
};

export default AddUserDialog;
